/**
 * fogPolygonUtils.js
 * GAP-003: Fog-of-war polygon/lasso geometry utilities.
 *
 * Pure functions — no side effects, no React deps, fully unit-testable.
 * Rule reference: GAP-003 spec — polygon/lasso shapes stored as normalized {xPct,yPct} arrays.
 * date_of_change: 2026-05-20
 */

/**
 * Ramer-Douglas-Peucker path simplification.
 * Reduces dense lasso point clouds to the minimal set of points that
 * approximate the original path within `epsilon` tolerance.
 *
 * @param {{ xPct: number, yPct: number }[]} points
 * @param {number} epsilon - Tolerance in the same units as points
 * @returns {{ xPct: number, yPct: number }[]}
 */
export const rdpSimplify = (points, epsilon) => {
  if (!Array.isArray(points) || points.length <= 2) return points;

  // Perpendicular distance from point p to line segment (a → b)
  const perpDist = (p, a, b) => {
    const dx = b.xPct - a.xPct;
    const dy = b.yPct - a.yPct;
    const lenSq = dx * dx + dy * dy;
    if (lenSq === 0) {
      const ddx = p.xPct - a.xPct;
      const ddy = p.yPct - a.yPct;
      return Math.sqrt(ddx * ddx + ddy * ddy);
    }
    const t = ((p.xPct - a.xPct) * dx + (p.yPct - a.yPct) * dy) / lenSq;
    const tClamped = Math.max(0, Math.min(1, t));
    const projX = a.xPct + tClamped * dx;
    const projY = a.yPct + tClamped * dy;
    const ex = p.xPct - projX;
    const ey = p.yPct - projY;
    return Math.sqrt(ex * ex + ey * ey);
  };

  const rdp = (pts, start, end) => {
    let maxDist = 0;
    let maxIdx = 0;
    for (let i = start + 1; i < end; i++) {
      const d = perpDist(pts[i], pts[start], pts[end]);
      if (d > maxDist) {
        maxDist = d;
        maxIdx = i;
      }
    }
    if (maxDist > epsilon) {
      const left = rdp(pts, start, maxIdx);
      const right = rdp(pts, maxIdx, end);
      return [...left.slice(0, -1), ...right];
    }
    return [pts[start], pts[end]];
  };

  return rdp(points, 0, points.length - 1);
};

/**
 * Validate that a normalized point array is suitable for a fog polygon operation.
 * Returns true if the array contains at least 3 distinct {xPct, yPct} objects.
 *
 * @param {any} pointsPct
 * @returns {boolean}
 */
export const isValidFogPolygon = (pointsPct) => {
  if (!Array.isArray(pointsPct) || pointsPct.length < 3) return false;
  return pointsPct.every(
    p => p !== null && typeof p === 'object' &&
         typeof p.xPct === 'number' && isFinite(p.xPct) &&
         typeof p.yPct === 'number' && isFinite(p.yPct)
  );
};

/**
 * Convert flat Konva points array [x1,y1,x2,y2,...] to normalized {xPct,yPct} array
 * given a map origin and size.
 *
 * @param {number[]} flatPts - [x1, y1, x2, y2, ...]
 * @param {{ x: number, y: number }} mapPosition
 * @param {{ width: number, height: number }} mapSize
 * @returns {{ xPct: number, yPct: number }[]}
 */
export const flatPtsToNormalized = (flatPts, mapPosition, mapSize) => {
  if (!Array.isArray(flatPts) || flatPts.length < 4) return [];
  const { x: mapX, y: mapY } = mapPosition;
  const { width: mapW, height: mapH } = mapSize;
  if (!mapW || !mapH) return [];

  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
  const result = [];
  for (let i = 0; i + 1 < flatPts.length; i += 2) {
    result.push({
      xPct: (clamp(flatPts[i], mapX, mapX + mapW) - mapX) / mapW,
      yPct: (clamp(flatPts[i + 1], mapY, mapY + mapH) - mapY) / mapH,
    });
  }
  return result;
};
